/**
 * Context Sanitizer Service
 *
 * Redacts sensitive information from captured context before submission.
 * Implements comprehensive pattern-based redaction with audit trail support.
 */

import type { CapturedContext, ConsoleError, NetworkError } from '../types';

/**
 * Redaction pattern definition with metadata for audit trail
 */
export interface RedactionPattern {
  pattern: RegExp;
  replacement: string;
  category: string;
  description: string;
}

/**
 * Redaction audit entry
 */
export interface RedactionAuditEntry {
  pattern: string;
  category: string;
  count: number;
  source: 'client' | 'server';
}

/**
 * Redaction summary for tracking what was redacted
 */
export interface RedactionSummary {
  totalRedactions: number;
  byCategory: Record<string, number>;
  entries: RedactionAuditEntry[];
  redactedAt: Date;
}

// Default patterns to always redact with metadata
const DEFAULT_REDACT_PATTERNS: RedactionPattern[] = [
  // Authentication
  {
    pattern: /bearer\s+[a-zA-Z0-9._-]+/gi,
    replacement: '[BEARER_TOKEN]',
    category: 'authentication',
    description: 'Bearer tokens',
  },
  {
    pattern: /token[=:]\s*["']?[a-zA-Z0-9._-]+["']?/gi,
    replacement: 'token=[TOKEN]',
    category: 'authentication',
    description: 'Generic tokens',
  },
  {
    pattern: /api[_-]?key[=:]\s*["']?[a-zA-Z0-9._-]+["']?/gi,
    replacement: 'api_key=[API_KEY]',
    category: 'authentication',
    description: 'API keys',
  },
  {
    pattern: /password[=:]\s*["']?[^"'\s]+["']?/gi,
    replacement: 'password=[REDACTED]',
    category: 'authentication',
    description: 'Passwords',
  },
  {
    pattern: /secret[=:]\s*["']?[a-zA-Z0-9._-]+["']?/gi,
    replacement: 'secret=[REDACTED]',
    category: 'authentication',
    description: 'Secrets',
  },

  // Financial identifiers
  {
    pattern: /account[_-]?(number|num|id|no)[=:]\s*["']?[\d-]+["']?/gi,
    replacement: '[ACCOUNT-XXX]',
    category: 'financial',
    description: 'Account numbers',
  },
  {
    pattern: /iban[=:]\s*["']?[A-Z]{2}\d{2}[A-Z0-9]+["']?/gi,
    replacement: '[IBAN]',
    category: 'financial',
    description: 'IBAN numbers',
  },

  // Credit card patterns (Luhn-valid formats)
  {
    pattern: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
    replacement: '[CARD-XXXX]',
    category: 'financial',
    description: 'Credit card numbers',
  },

  // Personal identifiers
  {
    pattern: /ssn[=:]\s*["']?[\d-]+["']?/gi,
    replacement: '[SSN]',
    category: 'pii',
    description: 'Social Security Numbers',
  },
  {
    pattern: /social[_-]?security[=:]\s*["']?[\d-]+["']?/gi,
    replacement: '[SSN]',
    category: 'pii',
    description: 'Social Security Numbers',
  },
  {
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    replacement: '[EMAIL]',
    category: 'pii',
    description: 'Email addresses',
  },
  {
    pattern: /\+?\d{1,3}[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
    replacement: '[PHONE]',
    category: 'pii',
    description: 'Phone numbers',
  },

  // Internal IP addresses (10.x.x.x, 172.16-31.x.x, 192.168.x.x)
  {
    pattern: /\b10\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
    replacement: '[INTERNAL_IP]',
    category: 'infrastructure',
    description: 'Internal IP addresses (10.x.x.x)',
  },
  {
    pattern: /\b172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}\b/g,
    replacement: '[INTERNAL_IP]',
    category: 'infrastructure',
    description: 'Internal IP addresses (172.16-31.x.x)',
  },
  {
    pattern: /\b192\.168\.\d{1,3}\.\d{1,3}\b/g,
    replacement: '[INTERNAL_IP]',
    category: 'infrastructure',
    description: 'Internal IP addresses (192.168.x.x)',
  },

  // AWS/Cloud credentials
  {
    pattern: /AKIA[0-9A-Z]{16}/g,
    replacement: '[AWS_ACCESS_KEY]',
    category: 'credentials',
    description: 'AWS Access Key IDs',
  },
  {
    pattern: /aws[_-]?(access|secret)[_-]?key[=:]\s*["']?[A-Za-z0-9/+=]+["']?/gi,
    replacement: '[AWS_KEY]',
    category: 'credentials',
    description: 'AWS keys',
  },

  // JWT tokens
  {
    pattern: /eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g,
    replacement: '[JWT_TOKEN]',
    category: 'authentication',
    description: 'JWT tokens',
  },

  // Database connection strings
  {
    pattern: /(postgres|mysql|mongodb|redis):\/\/[^@]+@[^\s]+/gi,
    replacement: '$1://[CREDENTIALS]@[HOST]',
    category: 'credentials',
    description: 'Database connection strings',
  },

  // Private keys
  {
    pattern: /-----BEGIN\s+(RSA\s+)?PRIVATE\s+KEY-----[\s\S]*?-----END\s+(RSA\s+)?PRIVATE\s+KEY-----/g,
    replacement: '[PRIVATE_KEY]',
    category: 'credentials',
    description: 'Private keys',
  },
];

// Legacy pattern array for backward compatibility
const LEGACY_PATTERNS: RegExp[] = DEFAULT_REDACT_PATTERNS.map((p) => p.pattern);

// Sensitive field names to redact in objects
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apiKey',
  'secret',
  'authorization',
  'auth',
  'credential',
  'credentials',
  'cookie',
  'session',
  'ssn',
  'social_security',
  'credit_card',
  'card_number',
  'cvv',
  'pin',
  'private_key',
  'privateKey',
  'counterparty',
  'counterparties',
  'iban',
  'swift_code',
  'routing_number',
];

/**
 * Configurable counterparty names to redact
 * Can be extended via createSanitizer() with custom list
 */
let configurableCounterparties: string[] = [];

/**
 * Set counterparty names to redact
 */
export function setCounterpartyList(counterparties: string[]): void {
  configurableCounterparties = counterparties.map((c) => c.toLowerCase());
}

/**
 * Create counterparty pattern from list
 */
function createCounterpartyPattern(counterparties: string[]): RedactionPattern | null {
  if (counterparties.length === 0) return null;

  // Escape special regex characters and join with |
  const escaped = counterparties.map((c) =>
    c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');

  return {
    pattern,
    replacement: '[COUNTERPARTY]',
    category: 'financial',
    description: 'Counterparty names',
  };
}

/**
 * Redact a string using patterns (legacy)
 */
function redactString(value: string, patterns: RegExp[]): string {
  let result = value;
  for (const pattern of patterns) {
    // Reset regex lastIndex for global patterns
    pattern.lastIndex = 0;
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

/**
 * Redact a string using patterns with audit trail
 */
function redactStringWithAudit(
  value: string,
  patterns: RedactionPattern[]
): { result: string; entries: RedactionAuditEntry[] } {
  let result = value;
  const entries: RedactionAuditEntry[] = [];

  for (const { pattern, replacement, category, description } of patterns) {
    // Reset regex lastIndex for global patterns
    pattern.lastIndex = 0;

    // Count matches before replacing
    const matches = value.match(pattern);
    if (matches && matches.length > 0) {
      entries.push({
        pattern: description,
        category,
        count: matches.length,
        source: 'client',
      });
      result = result.replace(pattern, replacement);
    }
  }

  return { result, entries };
}

/**
 * Recursively redact sensitive fields from an object
 */
function redactObject(
  obj: unknown,
  patterns: RegExp[],
  depth: number = 0
): unknown {
  // Prevent infinite recursion
  if (depth > 10) return '[MAX_DEPTH]';

  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    return redactString(obj, patterns);
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => redactObject(item, patterns, depth + 1));
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      // Check if key matches sensitive field names
      const isSensitive = SENSITIVE_FIELDS.some(
        (field) => key.toLowerCase().includes(field.toLowerCase())
      );

      if (isSensitive) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = redactObject(value, patterns, depth + 1);
      }
    }
    return result;
  }

  return obj;
}

/**
 * Sanitize console errors
 */
function sanitizeConsoleErrors(
  errors: ConsoleError[],
  patterns: RegExp[]
): ConsoleError[] {
  return errors.map((error) => ({
    ...error,
    message: redactString(error.message, patterns),
    stack: error.stack ? redactString(error.stack, patterns) : undefined,
  }));
}

/**
 * Sanitize network errors
 */
function sanitizeNetworkErrors(
  errors: NetworkError[],
  patterns: RegExp[]
): NetworkError[] {
  return errors.map((error) => ({
    ...error,
    url: redactString(error.url, patterns),
    responseBody: error.responseBody
      ? redactString(error.responseBody, patterns)
      : undefined,
    requestBody: error.requestBody
      ? redactString(error.requestBody, patterns)
      : undefined,
  }));
}

/**
 * Sanitize the full captured context
 */
export function sanitizeContext(
  context: CapturedContext,
  customPatterns: RegExp[] = []
): CapturedContext {
  const allPatterns = [...DEFAULT_REDACT_PATTERNS, ...customPatterns];

  return {
    ...context,
    // Sanitize page context
    page: {
      ...context.page,
      url: redactString(context.page.url, allPatterns),
      fullUrl: redactString(context.page.fullUrl, allPatterns),
      queryParams: context.page.queryParams
        ? (redactObject(context.page.queryParams, allPatterns) as Record<string, string>)
        : undefined,
    },
    // User info - keep structure but redact sensitive values
    user: context.user
      ? {
          id: context.user.id,
          role: context.user.role,
          email: context.user.email ? '[REDACTED]' : undefined,
          name: context.user.name,
        }
      : undefined,
    // Sanitize errors
    consoleErrors: sanitizeConsoleErrors(context.consoleErrors, allPatterns),
    networkErrors: sanitizeNetworkErrors(context.networkErrors, allPatterns),
    // Sanitize entities
    entities: context.entities.map((entity) => ({
      ...entity,
      metadata: entity.metadata
        ? (redactObject(entity.metadata, allPatterns) as Record<string, unknown>)
        : undefined,
    })),
  };
}

/**
 * Sanitizer configuration options
 */
export interface SanitizerConfig {
  customPatterns?: RedactionPattern[];
  counterparties?: string[];
  enableAudit?: boolean;
}

/**
 * Create a sanitizer function with custom patterns and configuration
 */
export function createSanitizer(config: SanitizerConfig = {}) {
  const { customPatterns = [], counterparties = [], enableAudit = true } = config;

  // Build pattern list
  const patterns: RedactionPattern[] = [...DEFAULT_REDACT_PATTERNS, ...customPatterns];

  // Add counterparty pattern if configured
  const counterpartyPattern = createCounterpartyPattern(counterparties);
  if (counterpartyPattern) {
    patterns.push(counterpartyPattern);
  }

  // Legacy patterns for backward compatibility
  const legacyPatterns = patterns.map((p) => p.pattern);

  return {
    /**
     * Sanitize full context with optional audit trail
     */
    sanitizeContext: (context: CapturedContext): CapturedContext & { redactionSummary?: RedactionSummary } => {
      if (enableAudit) {
        return sanitizeContextWithAudit(context, patterns);
      }
      return sanitizeContext(context, legacyPatterns.slice(DEFAULT_REDACT_PATTERNS.length));
    },

    /**
     * Sanitize a string value
     */
    sanitizeString: (value: string): string => {
      if (enableAudit) {
        return redactStringWithAudit(value, patterns).result;
      }
      return redactString(value, legacyPatterns);
    },

    /**
     * Sanitize an object recursively
     */
    sanitizeObject: (obj: unknown): unknown => redactObject(obj, legacyPatterns),

    /**
     * Sanitize string with audit trail
     */
    sanitizeStringWithAudit: (value: string) => redactStringWithAudit(value, patterns),

    /**
     * Get all active patterns
     */
    getPatterns: () => [...patterns],

    /**
     * Add a custom pattern at runtime
     */
    addPattern: (pattern: RedactionPattern) => {
      patterns.push(pattern);
    },
  };
}

/**
 * Sanitize context with full audit trail
 */
function sanitizeContextWithAudit(
  context: CapturedContext,
  patterns: RedactionPattern[]
): CapturedContext & { redactionSummary: RedactionSummary } {
  const allEntries: RedactionAuditEntry[] = [];
  const legacyPatterns = patterns.map((p) => p.pattern);

  // Helper to merge audit entries
  const addEntries = (entries: RedactionAuditEntry[]) => {
    for (const entry of entries) {
      const existing = allEntries.find(
        (e) => e.pattern === entry.pattern && e.category === entry.category
      );
      if (existing) {
        existing.count += entry.count;
      } else {
        allEntries.push({ ...entry });
      }
    }
  };

  // Sanitize page URL
  const { result: sanitizedUrl, entries: urlEntries } = redactStringWithAudit(
    context.page.url,
    patterns
  );
  addEntries(urlEntries);

  const { result: sanitizedFullUrl, entries: fullUrlEntries } = redactStringWithAudit(
    context.page.fullUrl,
    patterns
  );
  addEntries(fullUrlEntries);

  // Sanitize console errors
  const sanitizedConsoleErrors = context.consoleErrors.map((error) => {
    const { result: msg, entries: msgEntries } = redactStringWithAudit(error.message, patterns);
    addEntries(msgEntries);

    let sanitizedStack = error.stack;
    if (error.stack) {
      const { result: stack, entries: stackEntries } = redactStringWithAudit(error.stack, patterns);
      addEntries(stackEntries);
      sanitizedStack = stack;
    }

    return { ...error, message: msg, stack: sanitizedStack };
  });

  // Sanitize network errors
  const sanitizedNetworkErrors = context.networkErrors.map((error) => {
    const { result: url, entries: urlE } = redactStringWithAudit(error.url, patterns);
    addEntries(urlE);

    let sanitizedBody = error.responseBody;
    if (error.responseBody) {
      const { result: body, entries: bodyE } = redactStringWithAudit(error.responseBody, patterns);
      addEntries(bodyE);
      sanitizedBody = body;
    }

    let sanitizedRequestBody = error.requestBody;
    if (error.requestBody) {
      const { result: reqBody, entries: reqE } = redactStringWithAudit(error.requestBody, patterns);
      addEntries(reqE);
      sanitizedRequestBody = reqBody;
    }

    return {
      ...error,
      url,
      responseBody: sanitizedBody,
      requestBody: sanitizedRequestBody,
    };
  });

  // Build summary
  const byCategory: Record<string, number> = {};
  let totalRedactions = 0;
  for (const entry of allEntries) {
    byCategory[entry.category] = (byCategory[entry.category] || 0) + entry.count;
    totalRedactions += entry.count;
  }

  const redactionSummary: RedactionSummary = {
    totalRedactions,
    byCategory,
    entries: allEntries,
    redactedAt: new Date(),
  };

  return {
    ...context,
    page: {
      ...context.page,
      url: sanitizedUrl,
      fullUrl: sanitizedFullUrl,
      queryParams: context.page.queryParams
        ? (redactObject(context.page.queryParams, legacyPatterns) as Record<string, string>)
        : undefined,
    },
    user: context.user
      ? {
          id: context.user.id,
          role: context.user.role,
          email: context.user.email ? '[EMAIL]' : undefined,
          name: context.user.name,
        }
      : undefined,
    consoleErrors: sanitizedConsoleErrors,
    networkErrors: sanitizedNetworkErrors,
    entities: context.entities.map((entity) => ({
      ...entity,
      metadata: entity.metadata
        ? (redactObject(entity.metadata, legacyPatterns) as Record<string, unknown>)
        : undefined,
    })),
    redactionSummary,
  };
}

// Export types and functions
export {
  DEFAULT_REDACT_PATTERNS,
  SENSITIVE_FIELDS,
  redactStringWithAudit,
  sanitizeContextWithAudit,
};
